import React, { useState, useEffect, useRef, useCallback } from 'react';

// Shared decade data
export const decades = [
  {
    year: '1983',
    era: '1980s',
    title: 'Foundation',
    shortDesc: 'Family business established. First partnerships formed in footwear manufacturing.',
    description: 'LV1 was established as a family business, laying the groundwork for decades of footwear expertise. Early partnerships were formed with manufacturing facilities, beginning our journey in the footwear industry.',
    highlights: [
      'Family business established',
      'First manufacturing partnerships formed',
      'Foundation of sourcing relationships',
    ],
  },
  {
    year: '1990',
    era: '1990s',
    title: 'Expansion',
    shortDesc: 'Expanded operations and built long-term factory relationships across regions.',
    description: 'The decade of growth saw LV1 expand operations significantly. Long-term factory relationships were built across regions, establishing the multi-region sourcing capability that would become a cornerstone of our business.',
    highlights: [
      'Expanded manufacturing network',
      'Built multi-region partnerships',
      'Scaled operational capacity',
    ],
  },
  {
    year: '2000',
    era: '2000s',
    title: 'Portfolio Growth',
    shortDesc: 'Grew portfolio of licensed brands and developed multi-channel capabilities.',
    description: 'LV1 grew its portfolio of licensed brands and developed multi-channel retail capabilities. This era marked our evolution into a full-service footwear partner for major retailers.',
    highlights: [
      'Licensed brand portfolio expansion',
      'Multi-channel retail development',
      'Full-service capabilities established',
    ],
  },
  {
    year: '2010',
    era: '2010s',
    title: 'Global Scale',
    shortDesc: 'Scaled operations globally while maintaining family leadership and values.',
    description: 'Scaling operations globally while maintaining family leadership and core values. This period saw significant growth in volume and brand partnerships while preserving the relationships that define our business.',
    highlights: [
      'Global operational scale achieved',
      'Maintained family leadership',
      'Expanded brand partnerships',
    ],
  },
  {
    year: 'Now',
    era: 'Today',
    title: 'Leading Forward',
    shortDesc: 'Leading brand-driven footwear programs across mass, department, and specialty retail.',
    description: 'Today, LV1 leads brand-driven footwear programs across mass, department, and specialty retail. We continue to evolve while staying true to the family values and partner-first approach that started it all.',
    highlights: [
      'Leading brand-driven programs',
      'Multi-channel retail presence',
      'Continued family leadership',
    ],
  },
];

/**
 * DecadesTimeline - Shared animated timeline component
 *
 * The line and star glow appear BEHIND the nodes, never covering them.
 */
const DecadesTimeline = ({
  variant = 'full',
  nodeSize,
  onDecadeChange,
  initialActive = 4,
}) => {
  const [activeEra, setActiveEra] = useState(initialActive);
  const [hoveredEra, setHoveredEra] = useState(null);
  const [glowingNode, setGlowingNode] = useState(initialActive);
  const [isGlowPulsing, setIsGlowPulsing] = useState(false);
  const [isAutoAnimating, setIsAutoAnimating] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [hasAnimatedIn, setHasAnimatedIn] = useState(false);

  const timelineRef = useRef(null);
  const autoAnimateRef = useRef(null);
  const pulseTimeoutRef = useRef(null);

  const size = nodeSize || (variant === 'teaser' ? 48 : 64);
  const fontSize = variant === 'teaser' ? '11px' : '13px';
  const labelSize = variant === 'teaser' ? '12px' : '13px';

  // Check for reduced motion preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);
    const handleChange = (e) => setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // IntersectionObserver to detect when timeline is in view
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry.isIntersecting);
        if (entry.isIntersecting && !hasAnimatedIn) {
          setHasAnimatedIn(true);
        }
      },
      { threshold: 0.2 }
    );

    if (timelineRef.current) {
      observer.observe(timelineRef.current);
    }

    return () => observer.disconnect();
  }, [hasAnimatedIn]);

  // Trigger pulse animation on the glowing node
  const triggerPulse = useCallback(() => {
    setIsGlowPulsing(true);
    if (pulseTimeoutRef.current) clearTimeout(pulseTimeoutRef.current);
    pulseTimeoutRef.current = setTimeout(() => setIsGlowPulsing(false), 800);
  }, []);

  // Move glow to a specific node
  const moveGlowToNode = useCallback((index, withPulse = true) => {
    setGlowingNode(index);
    if (withPulse) {
      setTimeout(triggerPulse, 200);
    }
  }, [triggerPulse]);

  // Auto-animate glow along timeline (bounce back and forth) - SLOWER
  const startAutoAnimation = useCallback(() => {
    if (prefersReducedMotion || isAutoAnimating || !isInView) return;

    setIsAutoAnimating(true);
    let currentNode = 0;
    let direction = 1;

    const animateToNext = () => {
      if (!isInView) {
        setIsAutoAnimating(false);
        return;
      }

      moveGlowToNode(currentNode);

      const nextNode = currentNode + direction;
      if (nextNode >= decades.length) {
        direction = -1;
        currentNode = decades.length - 2;
      } else if (nextNode < 0) {
        direction = 1;
        currentNode = 1;
      } else {
        currentNode = nextNode;
      }

      // SLOWER: 2 seconds between nodes
      autoAnimateRef.current = setTimeout(animateToNext, 2000);
    };

    animateToNext();
  }, [prefersReducedMotion, isAutoAnimating, isInView, moveGlowToNode]);

  // Start auto-animation after line draws in
  useEffect(() => {
    if (hasAnimatedIn && !prefersReducedMotion && isInView) {
      setTimeout(() => {
        moveGlowToNode(0, false);
        setTimeout(startAutoAnimation, 1200);
      }, 1500);
    }
  }, [hasAnimatedIn, prefersReducedMotion, isInView, moveGlowToNode, startAutoAnimation]);

  // Pause auto-animation when out of view
  useEffect(() => {
    if (!isInView && autoAnimateRef.current) {
      clearTimeout(autoAnimateRef.current);
      setIsAutoAnimating(false);
    } else if (isInView && hasAnimatedIn && !isAutoAnimating && !prefersReducedMotion) {
      autoAnimateRef.current = setTimeout(startAutoAnimation, 2000);
    }
  }, [isInView, hasAnimatedIn, isAutoAnimating, prefersReducedMotion, startAutoAnimation]);

  // Cleanup timeouts
  useEffect(() => {
    return () => {
      if (autoAnimateRef.current) clearTimeout(autoAnimateRef.current);
      if (pulseTimeoutRef.current) clearTimeout(pulseTimeoutRef.current);
    };
  }, []);

  // Handle hover
  const handleNodeHover = (index) => {
    setHoveredEra(index);
    if (!prefersReducedMotion) {
      if (autoAnimateRef.current) {
        clearTimeout(autoAnimateRef.current);
        setIsAutoAnimating(false);
      }
      moveGlowToNode(index);
    }
  };

  // Handle hover leave
  const handleNodeLeave = () => {
    setHoveredEra(null);
    if (!prefersReducedMotion) {
      moveGlowToNode(activeEra, false);
      autoAnimateRef.current = setTimeout(startAutoAnimation, 4000);
    }
  };

  // Handle click
  const handleNodeClick = (index) => {
    setActiveEra(index);
    if (onDecadeChange) onDecadeChange(index);

    if (!prefersReducedMotion) {
      if (autoAnimateRef.current) {
        clearTimeout(autoAnimateRef.current);
        setIsAutoAnimating(false);
      }
      moveGlowToNode(index);
      autoAnimateRef.current = setTimeout(startAutoAnimation, 6000);
    }
  };

  return (
    <div ref={timelineRef} className="relative">
      {/* Timeline track container */}
      <div
        className="relative"
        style={{ height: `${size + 48}px` }}
      >
        {/* Timeline line - BEHIND nodes, doesn't go through them */}
        {/* Line segments between nodes */}
        <div
          className="absolute left-0 right-0 pointer-events-none"
          style={{
            top: `${size / 2}px`,
            height: '2px',
            zIndex: 0,
          }}
        >
          {/* Base line - faded, sits behind everything */}
          <div
            className="absolute inset-0"
            style={{
              background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.08) 10%, rgba(255,255,255,0.08) 90%, transparent 100%)',
              opacity: hasAnimatedIn ? 1 : 0,
              transition: 'opacity 0.6s ease',
            }}
          />

          {/* Shimmer effect - very subtle, behind nodes */}
          {!prefersReducedMotion && hasAnimatedIn && (
            <div
              className="absolute inset-0 overflow-hidden pointer-events-none"
              style={{ opacity: 0.25, zIndex: 0 }}
            >
              <div
                className="absolute h-full w-32 animate-timeline-shimmer"
                style={{
                  background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%)',
                }}
              />
            </div>
          )}
        </div>

        {/* Timeline nodes - ABOVE the line */}
        <div className="relative flex justify-between" style={{ zIndex: 2 }}>
          {decades.map((decade, index) => {
            const isActive = activeEra === index;
            const isHovered = hoveredEra === index;
            const isGlowing = glowingNode === index;

            return (
              <button
                key={decade.era}
                onClick={() => handleNodeClick(index)}
                onMouseEnter={() => handleNodeHover(index)}
                onMouseLeave={handleNodeLeave}
                className="relative flex flex-col items-center"
                style={{
                  opacity: hasAnimatedIn ? 1 : 0,
                  transform: hasAnimatedIn ? 'translateY(0)' : 'translateY(12px)',
                  transition: `opacity 0.7s ease ${index * 0.12 + 0.3}s, transform 0.7s ease ${index * 0.12 + 0.3}s`,
                }}
              >
                {/* Glow effect - BEHIND the node */}
                {!prefersReducedMotion && (
                  <div
                    className="absolute pointer-events-none"
                    style={{
                      width: `${size + 40}px`,
                      height: `${size + 40}px`,
                      left: '50%',
                      top: `${size / 2}px`,
                      transform: 'translate(-50%, -50%)',
                      background: 'radial-gradient(circle, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.08) 40%, transparent 70%)',
                      opacity: isGlowing ? (isGlowPulsing ? 1 : 0.7) : 0,
                      transition: 'opacity 0.8s ease',
                      zIndex: -1,
                      filter: 'blur(8px)',
                    }}
                  />
                )}

                {/* Pulse ring - BEHIND the node */}
                {!prefersReducedMotion && isGlowing && isGlowPulsing && (
                  <div
                    className="absolute pointer-events-none animate-glow-ring"
                    style={{
                      width: `${size}px`,
                      height: `${size}px`,
                      left: '50%',
                      top: `${size / 2}px`,
                      transform: 'translate(-50%, -50%)',
                      border: '2px solid rgba(255,255,255,0.3)',
                      borderRadius: '50%',
                      zIndex: -1,
                    }}
                  />
                )}

                {/* Node circle */}
                <div
                  className="relative rounded-full flex items-center justify-center"
                  style={{
                    width: `${size}px`,
                    height: `${size}px`,
                    background: isActive
                      ? 'white'
                      : isHovered
                        ? 'rgba(255,255,255,0.12)'
                        : 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.12)',
                    boxShadow: isActive
                      ? '0 0 24px rgba(255,255,255,0.25)'
                      : isHovered || isGlowing
                        ? '0 0 16px rgba(255,255,255,0.1)'
                        : 'none',
                    transform: isHovered && !isActive ? 'scale(1.04)' : 'scale(1)',
                    transition: 'all 0.4s ease',
                    zIndex: 2,
                  }}
                >
                  <span
                    className="font-medium"
                    style={{
                      fontSize,
                      color: isActive ? '#0a0a0a' : isHovered ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.6)',
                      transition: 'color 0.3s ease',
                    }}
                  >
                    {decade.year}
                  </span>
                </div>

                {/* Label */}
                <span
                  className="mt-4"
                  style={{
                    fontSize: labelSize,
                    color: isActive ? 'white' : isHovered ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.4)',
                    transform: isHovered ? 'scale(1.02)' : 'scale(1)',
                    transition: 'all 0.3s ease',
                  }}
                >
                  {decade.era}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* CSS Animations - SLOWER */}
      <style>{`
        @keyframes timeline-shimmer {
          0% { transform: translateX(-150px); }
          100% { transform: translateX(1400px); }
        }

        @keyframes glow-ring {
          0% {
            transform: translate(-50%, -50%) scale(1);
            opacity: 0.6;
          }
          100% {
            transform: translate(-50%, -50%) scale(1.5);
            opacity: 0;
          }
        }

        .animate-timeline-shimmer {
          animation: timeline-shimmer 10s linear infinite;
        }

        .animate-glow-ring {
          animation: glow-ring 0.8s ease-out forwards;
        }

        @media (prefers-reduced-motion: reduce) {
          .animate-timeline-shimmer,
          .animate-glow-ring {
            animation: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default DecadesTimeline;
